import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { fmtINR, fmtNum } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loading } from '@/components/Shared';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Fuel, Loader2, MapPin, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function OrderFlow() {
  const { productId } = useParams();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [step, setStep] = useState(1);
  const [qty, setQty] = useState('');
  const [addressId, setAddressId] = useState('');
  const [scheduleType] = useState('scheduled'); // scheduled delivery only (ASAP removed per business policy)
  const [schedDate, setSchedDate] = useState(() => new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [schedSlot, setSchedSlot] = useState('9');
  const [instructions, setInstructions] = useState('');
  const [payment, setPayment] = useState('Credit');
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [idemKey] = useState(() => Math.random().toString(36).slice(2));
  const [slotAvail, setSlotAvail] = useState(null); // { capacity, counts: { "<hour>": n } }

  useEffect(() => {
    api.get(`/products/${productId}`).then((r) => { setProduct(r.data); setQty(String(r.data.min_order_qty)); });
    api.get('/customers/me/addresses').then((r) => { setAddresses(r.data); const def = r.data.find((a) => a.is_default) || r.data[0]; if (def) setAddressId(def.id); });
  }, [productId]);

  const addr = addresses.find((a) => a.id === addressId);

  // Per-slot capacity + policy info for the selected date (from the backend).
  const slotInfo = (h) => {
    const s = slotAvail?.slots?.[String(h)];
    if (s) return { remaining: s.remaining, full: s.full, allowed: s.allowed, reason: s.reason };
    const cap = slotAvail?.capacity ?? 3;
    return { remaining: cap, full: false, allowed: true, reason: null };
  };
  const CUSTOMER_SLOTS = SLOTS.filter((s) => [9, 12, 15].includes(s.h));

  // Fetch slot availability whenever a scheduled date is chosen.
  useEffect(() => {
    if (scheduleType !== 'scheduled' || !schedDate) { setSlotAvail(null); return; }
    api.get(`/orders/slot-availability?date=${schedDate}`)
      .then((r) => setSlotAvail(r.data))
      .catch(() => setSlotAvail({ capacity: 3, counts: {} }));
  }, [scheduleType, schedDate]);

  // If the currently selected slot isn't bookable, jump to the first open one.
  useEffect(() => {
    if (scheduleType !== 'scheduled' || !slotAvail) return;
    if (!slotInfo(Number(schedSlot)).allowed) {
      const open = CUSTOMER_SLOTS.find((s) => slotInfo(s.h).allowed);
      if (open) setSchedSlot(String(open.h));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotAvail]);

  const getQuote = async () => {
    if (!addr) return toast.error('Add a delivery address first (Profile)');
    if (Number(qty) < product.min_order_qty) return toast.error(`Minimum ${product.min_order_qty} ${product.unit}`);
    setBusy(true);
    try {
      const r = await api.post('/orders/quote', { product_id: productId, qty: Number(qty), lat: addr.lat, lng: addr.lng });
      setQuote(r.data); setStep(3);
    } catch (e) { toast.error('Failed to get quote'); } finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!schedDate) return toast.error('Pick a delivery date');
    setBusy(true);
    try {
      const slot = SLOTS.find((s) => String(s.h) === schedSlot) || SLOTS[1];
      const dt = new Date(`${schedDate}T${String(slot.h).padStart(2, '0')}:00:00`);
      const scheduledAt = dt.toISOString();
      const pretty = dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      const label = `${pretty} · ${slot.label}`;
      const r = await api.post('/orders', {
        product_id: productId, qty: Number(qty), address_id: addressId, lat: addr.lat, lng: addr.lng,
        address_text: addr.address, delivery_time: label, schedule_type: 'scheduled',
        scheduled_at: scheduledAt,
        slot_date: schedDate,
        slot_hour: slot.h,
        priority: 'normal', instructions, payment_method: payment, idempotency_key: idemKey,
      });
      toast.success('Order placed!');
      nav(`/app/track/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to place order'); } finally { setBusy(false); }
  };

  if (!product) return <Loading />;

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center"><Fuel className="h-6 w-6 text-primary" /></div>
        <div><h2 className="font-display text-xl font-semibold">{product.name}</h2><p className="text-sm text-muted-foreground">{fmtINR(product.price)}/{product.unit} · GST {product.gst}%</p></div>
      </div>

      <div className="flex items-center gap-2 text-xs">
        {[1, 2, 3].map((s) => <div key={s} className={`flex-1 h-1.5 rounded-full ${step >= s ? 'bg-primary' : 'bg-muted'}`} />)}
      </div>

      {step === 1 && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-4">
          <div><Label>Quantity ({product.unit})</Label><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="order-flow-quantity-input" /><p className="text-xs text-muted-foreground mt-1">Min {fmtNum(product.min_order_qty)} {product.unit}</p></div>
          <div className="flex gap-2">{[1000, 2000, 5000].map((q) => <Button key={q} type="button" variant="secondary" size="sm" onClick={() => setQty(String(q))}>{fmtNum(q)}</Button>)}</div>
          <Button className="w-full h-11" onClick={() => setStep(2)} data-testid="order-flow-next-1">Continue</Button>
        </div>
      )}

      {step === 2 && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-4">
          <div>
            <Label>Delivery Location</Label>
            {addresses.length === 0 ? (
              <div className="text-sm text-muted-foreground mt-2">No address. <Button variant="link" className="px-1" onClick={() => nav('/app/profile')}>Add one</Button></div>
            ) : (
              <Select value={addressId} onValueChange={setAddressId}>
                <SelectTrigger data-testid="order-flow-address-select" className="mt-1"><SelectValue placeholder="Select address" /></SelectTrigger>
                <SelectContent>{addresses.map((a) => <SelectItem key={a.id} value={a.id}>{a.label} — {a.address}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label>Delivery Schedule</Label>
            <p className="text-xs text-muted-foreground mt-1">All deliveries are scheduled — pick a date and time slot.</p>
            <div className="grid grid-cols-2 gap-2 mt-2">
                <div><Label className="text-xs">Date</Label><Input type="date" min={new Date().toISOString().slice(0, 10)} value={schedDate} onChange={(e) => setSchedDate(e.target.value)} data-testid="schedule-date-input" /></div>
                <div><Label className="text-xs">Time Slot</Label>
                  <Select value={schedSlot} onValueChange={setSchedSlot}>
                    <SelectTrigger data-testid="schedule-slot-select"><SelectValue placeholder="Slot" /></SelectTrigger>
                    <SelectContent>{CUSTOMER_SLOTS.map((s) => { const info = slotInfo(s.h); return (
                      <SelectItem key={s.h} value={String(s.h)} disabled={!info.allowed} data-testid={`slot-option-${s.h}`}>
                        {s.label} {info.full ? '— Full' : (info.allowed ? `— ${info.remaining} left` : '— Unavailable')}
                      </SelectItem>
                    ); })}</SelectContent>
                  </Select>
                  {slotAvail && !CUSTOMER_SLOTS.some((s) => slotInfo(s.h).allowed) && (
                    <p className="text-[11px] text-destructive mt-1" data-testid="slot-none-available">No slots available for this date (same-day booking closes at 3 PM). Please pick the next day.</p>
                  )}
                </div>
              </div>
          </div>
          <div><Label>Delivery Instructions</Label><Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="e.g. Call security on arrival" data-testid="order-flow-instructions-input" /></div>
          <div>
            <Label>Payment Method</Label>
            <RadioGroup value={payment} onValueChange={setPayment} className="grid grid-cols-2 gap-2 mt-2">
              {['Credit', 'UPI', 'Net Banking', 'Cash'].map((m) => (
                <label key={m} className={`flex items-center gap-2 rounded-lg border p-2.5 cursor-pointer ${payment === m ? 'border-primary bg-muted' : 'border-border'}`}>
                  <RadioGroupItem value={m} data-testid={`pay-${m.replace(/\s+/g, '-').toLowerCase()}`} /><span className="text-sm">{m}</span>
                </label>
              ))}
            </RadioGroup>
          </div>
          <div className="flex gap-2"><Button variant="secondary" className="flex-1" onClick={() => setStep(1)}>Back</Button><Button className="flex-1 h-11" onClick={getQuote} disabled={busy || !addressId} data-testid="order-flow-review-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Review Order'}</Button></div>
        </div>
      )}

      {step === 3 && quote && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="order-review-card">
          <p className="font-semibold">Order Summary</p>
          <div className="flex items-start gap-2 text-sm"><MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" /><div><p>{addr?.label}</p><p className="text-muted-foreground">{addr?.address}</p></div></div>
          <div className="border-t border-border pt-3 space-y-1.5 text-sm">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Fuel Invoice</p>
            <Row l={`${fmtNum(quote.qty)} ${quote.unit} × ${fmtINR(quote.unit_price)}`} r={fmtINR(quote.subtotal)} />
            {quote.price_source === 'customer_specific' && <p className="text-xs text-primary">Special contract price applied</p>}
            {quote.product_gst_pct > 0
              ? <Row l={`GST @ ${quote.product_gst_pct}%`} r={fmtINR(quote.product_gst_amount)} />
              : <p className="text-xs text-muted-foreground">Diesel is exempt from GST in India (Nil)</p>}
            <Row l="Fuel Total" r={fmtINR(quote.product_total)} />
            <div className="border-t border-dashed border-border pt-2 mt-1" />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Delivery Invoice (separate)</p>
            <Row l={`Delivery Charge (${quote.distance_km} km)`} r={quote.delivery_charge === 0 ? 'FREE' : fmtINR(quote.delivery_charge)} />
            <Row l={`GST @ ${quote.delivery_gst_pct}%`} r={fmtINR(quote.delivery_gst_amount)} />
            <Row l="Delivery Total" r={fmtINR(quote.delivery_total)} />
            <div className="border-t border-border pt-2 flex justify-between font-semibold text-base"><span>Grand Total</span><span data-testid="order-total-value">{fmtINR(quote.total)}</span></div>
          </div>
          <div className="flex gap-2"><Button variant="secondary" className="flex-1" onClick={() => setStep(2)}>Back</Button><Button className="flex-1 h-12" onClick={confirm} disabled={busy} data-testid="order-flow-confirm-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-2" />Confirm Order</>}</Button></div>
        </div>
      )}
    </div>
  );
}

const Row = ({ l, r }) => <div className="flex justify-between"><span className="text-muted-foreground">{l}</span><span>{r}</span></div>;

const SLOTS = [
  { label: '06:00 AM - 09:00 AM', h: 6 },
  { label: '09:00 AM - 12:00 PM', h: 9 },
  { label: '12:00 PM - 03:00 PM', h: 12 },
  { label: '03:00 PM - 09:00 PM', h: 15 },
  { label: '09:00 PM - 12:00 AM', h: 21 },
  { label: '12:00 AM - 06:00 AM', h: 0 },
];
